import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import UserProfileDashboard from './UserProfileDashboard';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { apiService } from '../services/apiService';
import { useLocation, useSearchParams } from 'react-router-dom';
import { DFT_BRAND, HEADER_HEIGHT_PX, APP_PAGE_ZOOM_VAR } from '../styles/brandPalette';

export interface HeaderProps {
  /** Minimal header for super-admin-only shell (no org branding / fetch). */
  superAdminMode?: boolean;
  sidebarOpen?: boolean;
  onToggleSidebar?: () => void;
}

/** Strip leading/trailing whitespace from org name (or slug fallback) for consistent header display. */
function trimOrganizationDisplayName(name?: string | null, slug?: string | null): string {
  const trimmedName = name != null ? String(name).trim() : '';
  if (trimmedName) return trimmedName;
  return slug != null ? String(slug).trim() : '';
}

const PAGE_TITLES: { prefix: string; title: string }[] = [
  { prefix: '/ai-agent', title: 'AI Agent' },
  { prefix: '/projects', title: 'Projects' },
  { prefix: '/environments', title: 'Environments' },
  { prefix: '/swagger', title: 'Swagger' },
  { prefix: '/collections', title: 'Collections' },
  { prefix: '/test-plans', title: 'Test Plan' },
  { prefix: '/test-cycles', title: 'Test Plan' },
  { prefix: '/env-variables', title: '.Env' },
  { prefix: '/functions', title: 'Functions' },
  { prefix: '/proxy-settings', title: 'Proxy Settings' },
  { prefix: '/console', title: 'Console' },
  { prefix: '/help', title: 'Help' },
  { prefix: '/manage-account', title: 'Manage Account' },
  { prefix: '/history', title: 'History' },
  { prefix: '/profile', title: 'Profile' },
  { prefix: '/trash', title: 'Recycle Bin' },
  { prefix: '/pricing', title: 'Pricing' },
  { prefix: '/payment', title: 'Payment' },
  { prefix: '/super-admin/environment-management', title: 'Environment Management' },
  { prefix: '/super-admin/user-management', title: 'User Management' },
  { prefix: '/super-admin/tenant-management', title: 'Tenants' },
  { prefix: '/super-admin/plan-management', title: 'Plan Management' },
  { prefix: '/super-admin/grievance', title: 'Grievance' },
  { prefix: '/super-admin/revenue', title: 'Revenue' },
  { prefix: '/super-admin/rbac', title: 'RBAC' },
  { prefix: '/super-admin/alerts', title: 'Alerts' },
  { prefix: '/super-admin/advanced-monitoring', title: 'Advanced Monitoring' },
  { prefix: '/super-admin/system-configuration', title: 'System Configuration' },
  { prefix: '/super-admin/data-reports', title: 'Data & Reports' },
  { prefix: '/super-admin/security-controls', title: 'Security Controls' },
  { prefix: '/super-admin/login-history', title: 'Login History' },
  { prefix: '/super-admin/activity-logs', title: 'Activity' },
  { prefix: '/super-admin/user-details', title: 'User Stats' },
  { prefix: '/super-admin/dashboard', title: 'Dashboard' },
  { prefix: '/dashboard', title: 'Dashboard' },
  { prefix: '/', title: 'Dashboard' },
];

function titleForPath(pathname: string): string {
  const match = PAGE_TITLES.find((entry) =>
    entry.prefix === '/' ? pathname === '/' : pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`)
  );
  return match?.title ?? 'Dashboard';
}

const Header: React.FC<HeaderProps> = ({
  superAdminMode = false,
  sidebarOpen = true,
  onToggleSidebar,
}) => {
  const { isAuthenticated, user } = useAuth();
  const { organization, setOrganization } = useTenant();
  const [organizationName, setOrganizationName] = useState<string>('');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const collectionsSearchRef = useRef<HTMLInputElement>(null);

  const showCollectionsSearch = !superAdminMode && location.pathname === '/collections';
  const collectionsQuery = searchParams.get('q') ?? '';
  const pageTitle = titleForPath(location.pathname);

  const setCollectionsQuery = useCallback(
    (value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value.trim()) next.set('q', value);
      else next.delete('q');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (!showCollectionsSearch) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        collectionsSearchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showCollectionsSearch]);

  useEffect(() => {
    if (superAdminMode) return;
    let cancelled = false;

    const applyOrg = (org: { id?: number; name?: string; slug?: string; planType?: string; role?: string } | null) => {
      if (!org || cancelled) return false;
      const label = trimOrganizationDisplayName(org.name, org.slug);
      if (!label) return false;
      setOrganizationName(label);
      if (org.id != null && org.slug) {
        setOrganization({
          id: org.id,
          name: (org.name != null ? String(org.name).trim() : '') || label,
          slug: String(org.slug),
          planType: org.planType || 'FREE',
          role: org.role,
        });
      }
      return true;
    };

    const loadOrganization = async () => {
      if (!isAuthenticated) {
        setOrganizationName('');
        return;
      }

      const contextName = organization?.name != null ? String(organization.name).trim() : '';
      if (contextName) {
        setOrganizationName(contextName);
        return;
      }

      try {
        const primaryOrg = await apiService.getPrimaryOrganization();
        if (applyOrg(primaryOrg)) return;
      } catch (error) {
        console.warn('Primary organization unavailable, trying membership list:', error);
      }

      try {
        const orgs = await apiService.getOrganizations();
        if (Array.isArray(orgs) && orgs.length > 0) {
          const hostSlug = (organization?.slug || user?.organizationSlug || '').trim().toLowerCase();
          const matched =
            (hostSlug && orgs.find((o: any) => String(o?.slug || '').toLowerCase() === hostSlug)) ||
            orgs[0];
          if (applyOrg(matched)) return;
        }
      } catch (error) {
        console.error('Failed to load organizations:', error);
      }

      const slugFallback = trimOrganizationDisplayName(
        null,
        organization?.slug || user?.organizationSlug || null
      );
      if (!cancelled && slugFallback) {
        setOrganizationName(slugFallback);
      }
    };

    void loadOrganization();
    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    organization?.id,
    organization?.name,
    organization?.slug,
    user?.organizationSlug,
    setOrganization,
    superAdminMode,
  ]);

  const crumbRoot = superAdminMode
    ? 'Super Admin'
    : isAuthenticated && organizationName.trim()
      ? organizationName.trim()
      : 'CRUD Expert';

  return (
    <AppBar
      position="relative"
      elevation={0}
      sx={{
        flexShrink: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        backgroundColor: DFT_BRAND.paper,
        color: DFT_BRAND.text,
        borderBottom: `1px solid ${DFT_BRAND.line}`,
        boxShadow: 'none',
        zoom: { xs: 1, md: APP_PAGE_ZOOM_VAR },
      }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          minHeight: `${HEADER_HEIGHT_PX}px !important`,
          maxHeight: `${HEADER_HEIGHT_PX}px !important`,
          height: `${HEADER_HEIGHT_PX}px !important`,
          px: { xs: 1.5, sm: 3 },
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
            flex: 1,
          }}
        >
          {onToggleSidebar && (
            <IconButton
              onClick={onToggleSidebar}
              aria-label={sidebarOpen ? 'Collapse navigation' : 'Open navigation'}
              aria-expanded={sidebarOpen}
              sx={{
                width: 36,
                height: 36,
                border: `1px solid ${DFT_BRAND.line}`,
                borderRadius: '8px',
                color: DFT_BRAND.text,
                backgroundColor: DFT_BRAND.paper,
                '&:hover': {
                  backgroundColor: DFT_BRAND.tint,
                },
              }}
            >
              <MenuIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <Typography
            noWrap
            sx={{
              color: DFT_BRAND.muted,
              fontSize: '13px !important',
              minWidth: 0,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {crumbRoot}
            <Box component="span" sx={{ px: 1.25, color: DFT_BRAND.muted }}>
              /
            </Box>
            <Box component="span" sx={{ fontWeight: 500, color: DFT_BRAND.text }}>
              {pageTitle}
            </Box>
          </Typography>
        </Box>

        {showCollectionsSearch && (
          <Box
            sx={{
              flex: 1,
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: 0,
              px: 1,
            }}
          >
            <TextField
              inputRef={collectionsSearchRef}
              placeholder="Search collections, requests, APIs..."
              value={collectionsQuery}
              onChange={(e) => setCollectionsQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: DFT_BRAND.muted, fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color: DFT_BRAND.muted,
                        userSelect: 'none',
                        fontSize: '0.7rem !important',
                        border: `1px solid ${DFT_BRAND.line}`,
                        borderRadius: 1,
                        px: 0.75,
                        py: 0.125,
                      }}
                    >
                      Ctrl K
                    </Typography>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '100%',
                maxWidth: 280,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: DFT_BRAND.bg,
                  height: 40,
                  fontSize: '0.875rem',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: DFT_BRAND.line,
                },
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <UserProfileDashboard />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
